<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import MetricCard from '@/components/MetricCard.vue';
import PageState from '@/components/PageState.vue';
import ProjectCard from '@/components/ProjectCard.vue';
import ProjectHealthDetails from '@/components/ProjectHealthDetails.vue';
import StatusChip from '@/components/StatusChip.vue';
import { useSession } from '@/auth/session';
import { projectShortcuts, visibleModulesFor } from '@/data/modules';
import { usePortfolio } from '@/composables/usePortfolio';
import { formatDate } from '@/utils/format';
import { duplicateProjectNumbers, healthFor, healthLabel, healthReasonText, summaryFor , milestonesFor} from '@/utils/portfolio';

const { data, loading, error, load } = usePortfolio();
const session = useSession();
/* 和侧栏、模块中心同一把尺子：管理员专属模块不摆在首页 */
const visibleModules = computed(() => visibleModulesFor(session.canManageUsers.value));
/* 标题里的数字跟着实际渲染的条数走。原来写死 09，普通人看到的其实是 07 */
const moduleCountLabel = computed(() => String(visibleModules.value.length).padStart(2, '0'));
const modulesExpanded = ref(true);
/** 项目卡上的「查看明细」。组合页早就接了这个事件，指挥台一直漏着——点了没反应 */
const selectedHealthProjectId = ref<string | number | null>(null);
const selectedHealthProject = computed(() => (
  data.value?.projects.find(
    (project) => String(project.id) === String(selectedHealthProjectId.value),
  )
));

onMounted(() => {
  modulesExpanded.value = !window.matchMedia('(max-width: 760px)').matches;
});

const syncModulesExpanded = (event: Event) => {
  modulesExpanded.value = (event.currentTarget as HTMLDetailsElement).open;
};

/*
  项目内常用入口先得认一个项目。顺序：上次选的 → 我参与的第一个 → 列表第一个。
  记住上次选择，是因为工程师基本天天泡在同一个项目里；
  localStorage 读写都包起来——隐私窗口和清过站点数据的浏览器会直接抛，
  不能让首页跟着白屏。归档、删除、取消的项目不进这张单子。
*/
const SHORTCUT_PROJECT_KEY = 'pdm.command.shortcutProject';
const readRememberedProject = (): string => {
  try {
    return window.localStorage.getItem(SHORTCUT_PROJECT_KEY) || '';
  } catch {
    return '';
  }
};
const pickedProjectId = ref(readRememberedProject());
const shortcutProjects = computed(() => {
  if (!data.value) return [];
  return [...data.value.projects]
    .filter((project) => !project.archived && !project.deleted
      && !['archived', 'deleted', 'cancelled'].includes(String(project.status || '')))
    .sort((a, b) =>
      Number(Boolean(b.isCurrentUserMember)) - Number(Boolean(a.isCurrentUserMember))
      || String(a.projectNo).localeCompare(String(b.projectNo)));
});
const shortcutProjectId = computed<string>({
  get() {
    const list = shortcutProjects.value;
    const remembered = list.find((project) => String(project.id) === pickedProjectId.value);
    return String((remembered || list[0])?.id ?? '');
  },
  set(value: string) {
    pickedProjectId.value = value;
    try {
      window.localStorage.setItem(SHORTCUT_PROJECT_KEY, value);
    } catch {
      /* 存不下就只在这次会话里生效，不影响跳转 */
    }
  },
});

const priorityProjects = computed(() => {
  if (!data.value) return [];
  const rank = { risk: 0, warning: 1, normal: 2 };
  return [...data.value.projects]
    .sort((a, b) =>
      Number(Boolean(b.isCurrentUserMember)) - Number(Boolean(a.isCurrentUserMember))
      || (rank[healthFor(data.value!, a.id)?.status || 'normal'] - rank[healthFor(data.value!, b.id)?.status || 'normal']),
    )
    .slice(0, 6);
});

const conflicts = computed(() => duplicateProjectNumbers(data.value?.projects || []));
/** 有异常的项目，全部；列表只露前 7 个，剩下的用一行说清楚 */
const exceptionProjects = computed(() => {
  if (!data.value) return [];
  return data.value.projects
    .map((project) => ({ project, health: healthFor(data.value!, project.id) }))
    .filter((item) => item.health?.status === 'risk' || item.health?.status === 'warning');
});
const EXCEPTION_PREVIEW = 7;
const exceptions = computed(() => exceptionProjects.value.slice(0, EXCEPTION_PREVIEW));
/*
  截断要写出来。线上 18 个项目里 12 个有异常，只露 7 个又不说，
  人会以为异常就这几个——和组合页分页藏掉 6 个项目是同一类问题。
*/
const hiddenExceptions = computed(() =>
  Math.max(0, exceptionProjects.value.length - EXCEPTION_PREVIEW));
/* 健康分布卡底部那句话。红灯为 0 不等于没异常：有黄灯时不能再说「当前无已识别异常项目」 */
const healthFooterText = computed(() => {
  const counts = data.value?.metrics.healthCounts;
  if (!counts) return '';
  if (counts.risk) return `${counts.risk} 个项目需优先处置`;
  if (counts.warning) return '暂无红灯项目，黄灯项目需关注';
  return '当前无红灯/黄灯项目';
});
</script>

<template>
  <!-- 已经有数据时刷新不切成「加载中」：切了会把打开着的健康度明细拆掉重建（0930，见 refresh-must-not-remount-panel） -->
  <PageState v-if="loading && !data" state="loading" title="正在形成公司级态势" message="读取项目、计划、里程碑与健康度。" />
  <PageState v-else-if="error" state="error" title="项目组合数据不可用" message="下方是接口返回的原因；重试仍失败请联系管理员。" :detail="error" @retry="load" />
  <template v-else-if="data">
    <section class="command-hero">
      <div class="hero-copy">
        <span class="eyebrow">PORTFOLIO COMMAND CENTER / {{ formatDate(data.asOf) }}</span>
        <h1>先看异常，<br /><em>再做决策。</em></h1>
        <!--
          数的是**已启动**的项目（0915）。草稿的「逾期」是标准模板自带的日期，
          不是谁真拖了，算进来会让红灯和逾期数虚高——大屏按同一条判据算，两边这才对得上。
        -->
        <p>
          {{ data.metrics.startedProjects }} 个在管项目的健康度、计划完整性与逾期里程碑，都在这一页。点任意数字可以直接查到是哪些项目。
          <template v-if="data.metrics.notStartedProjects">
            另有 {{ data.metrics.notStartedProjects }} 个项目还是草稿、没启动，不计入上面这些数。
          </template>
        </p>
        <div class="hero-actions">
          <RouterLink class="button button-primary" to="/portfolio">打开全部项目</RouterLink>
          <RouterLink class="text-link" to="/my-work">打开我的工作 <b>↘</b></RouterLink>
        </div>
      </div>
      <article class="health-board">
        <header><span>PORTFOLIO HEALTH</span><time>{{ formatDate(data.asOf) }}</time></header>
        <h2>组合健康分布</h2>
        <!-- 三个数字整块可点，落到已经按健康度筛好的项目列表 -->
        <div class="health-numbers">
          <RouterLink class="risk" to="/projects?health=risk&scope=all&stage=started" aria-label="查看全部异常项目">
            <strong>{{ data.metrics.healthCounts.risk }}</strong><span>红灯</span><em>查看 →</em>
          </RouterLink>
          <RouterLink class="warning" to="/projects?health=warning&scope=all&stage=started" aria-label="查看全部关注项目">
            <strong>{{ data.metrics.healthCounts.warning }}</strong><span>黄灯</span><em>查看 →</em>
          </RouterLink>
          <RouterLink class="normal" to="/projects?health=normal&scope=all&stage=started" aria-label="查看全部正常项目">
            <strong>{{ data.metrics.healthCounts.normal }}</strong><span>绿灯</span><em>查看 →</em>
          </RouterLink>
        </div>
        <footer>
          <b>{{ healthFooterText }}</b>
          <span>健康度用于管理定位，不替代正式 Gate 评审。</span>
        </footer>
      </article>
    </section>

    <section class="metric-grid">
      <MetricCard
        index="01" label="在管项目" :value="data.metrics.startedProjects"
        :note="data.metrics.notStartedProjects
          ? `另有 ${data.metrics.notStartedProjects} 个草稿未启动，不计入异常数`
          : '已启动的项目'"
        to="/projects?scope=all" link-hint="查看全部项目"
      />
      <MetricCard
        index="02" label="已配置计划" :value="data.metrics.plannedProjects"
        note="已排出阶段与节点" to="/projects?plan=planned&stage=started" link-hint="查看这些项目"
      />
      <MetricCard
        index="03" label="待补计划" :value="data.metrics.projectsWithoutPlan"
        note="还没有任何阶段计划" tone="warning" to="/projects?plan=missing&stage=started" link-hint="去补计划"
      />
      <!-- 数的是里程碑条数，点进去是含有这些里程碑的项目，提示语必须说清楚 -->
      <MetricCard
        index="04" label="逾期里程碑" :value="data.metrics.overdueMilestones"
        note="已过计划日期仍未完成" tone="risk" to="/projects?milestone=overdue&stage=started" link-hint="查看涉及的项目"
      />
    </section>

    <section class="content-panel">
      <header class="section-heading">
        <div><span>MY PROJECTS BY PRIORITY</span><h2>我的项目优先</h2><p>我参与的项目排在前面，其余按异常程度排序。</p></div>
        <RouterLink class="text-link" to="/portfolio">全部项目 →</RouterLink>
      </header>
      <div class="project-grid">
        <ProjectCard
          v-for="project in priorityProjects"
          :key="project.id"
          :project="project"
          :summary="summaryFor(data, project.id)"
          :health="healthFor(data, project.id)"
          :milestones="milestonesFor(data, project.id)"
          :conflict="conflicts.has(project.projectNo)"
          @inspect-health="selectedHealthProjectId = project.id"
        />
      </div>
      <ProjectHealthDetails
        v-if="selectedHealthProject"
        :project="selectedHealthProject"
        :health="healthFor(data, selectedHealthProject.id)"
        :summary="summaryFor(data, selectedHealthProject.id)"
        :milestones="data.milestones"
        :projects="data.projects"
        :as-of="data.asOf"
        @close="selectedHealthProjectId = null"
        @declared="load"
      />
    </section>

    <div class="two-column">
      <section class="content-panel">
        <header class="section-heading"><div><span>EXCEPTIONS</span><h2>异常事项</h2></div><small>红黄仅用于异常语义</small></header>
        <ol class="exception-list">
          <li v-for="item in exceptions" :key="item.project.id">
            <RouterLink :to="`/projects/${item.project.id}/overview`">
              <StatusChip :value="item.health?.status" :label="healthLabel(item.health?.status)" />
              <span class="exception-body">
                <b>{{ item.project.projectNo }} · {{ item.project.projectName }}</b>
                <span>{{ [...(item.health?.reasons || []), ...(item.health?.dataIssues || [])].map(healthReasonText).join('；') || '未提供异常说明' }}</span>
              </span>
            </RouterLink>
          </li>
          <li v-if="!exceptions.length" class="empty-row">当前没有异常项目。</li>
          <li v-if="hiddenExceptions" class="exception-more">
            还有 {{ hiddenExceptions }} 个项目也有异常
            <RouterLink class="text-link" to="/portfolio">在项目组合里看全部 →</RouterLink>
          </li>
        </ol>
      </section>
      <details
        class="content-panel module-entry-panel"
        :open="modulesExpanded"
        @toggle="syncModulesExpanded"
      >
        <summary class="section-heading">
          <div>
            <span>MODULE SHORTCUTS / {{ moduleCountLabel }}</span>
            <h2>模块快速入口</h2>
            <p>上排是按权限开放的业务模块，下排是项目里最常打开的页——先选项目再点。</p>
          </div>
          <span class="module-disclosure" aria-hidden="true"></span>
        </summary>
        <div class="module-compact-grid">
          <RouterLink v-for="module in visibleModules" :key="module.key" :to="module.route">
            <b>{{ module.code }}</b><span>{{ module.name }}</span>
          </RouterLink>
        </div>
        <div class="shortcut-project">
          <label for="shortcut-project">项目内常用</label>
          <select id="shortcut-project" v-model="shortcutProjectId" :disabled="!shortcutProjects.length">
            <option v-for="project in shortcutProjects" :key="project.id" :value="String(project.id)">
              {{ project.projectNo }} · {{ project.projectName }}{{ project.isCurrentUserMember ? '（我参与）' : '' }}
            </option>
          </select>
        </div>
        <div v-if="shortcutProjectId" class="module-compact-grid">
          <RouterLink
            v-for="shortcut in projectShortcuts"
            :key="shortcut.suffix"
            :to="`/projects/${shortcutProjectId}/${shortcut.suffix}`"
          >
            <b>{{ shortcut.code }}</b><span>{{ shortcut.label }}</span>
          </RouterLink>
        </div>
        <p v-else class="shortcut-empty">还没有可进入的项目，先在项目组合里建一个。</p>
        <div class="module-entry-actions">
          <RouterLink class="text-link" to="/modules">查看全部模块 →</RouterLink>
        </div>
      </details>
    </div>
  </template>
</template>
